/**
 * Lightweight todo preview for Todos table side panel (not full BasecampTodoViewer).
 * Mimics todo viewer summary + secondary-panel work spec list / drill-in.
 * Bet: docs/product/bets/2026-06-18-basecamp-todos-explorer-interaction-and-activity-cache.md
 */

import { useCallback, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PreviewPaneHeader } from '@/components/features/preview-pane-header';
import { MarkdownPreview } from '@/components/features/markdown-preview';
import {
  BASECAMP_DUE_BADGE_COMPACT_CLASS,
  BasecampDueBadge,
  formatBasecampDueAbsolute,
} from './components/basecamp-due-badge';
import { TodoDescription } from './components/todo-description';
import { useBasecampTodoRefreshEvents } from './hooks/use-basecamp-todo-refresh-events';
import { basecampApi } from '@/lib/basecamp-api';
import { fetchWithAuth } from '@/lib/api';
import { testIds } from '@/lib/test-ids';
import { SHELL_COLUMN_FOOTER_CLASS } from '@/lib/shell-chrome';
import { cn } from '@/lib/utils';
import { isBasecampDueOverdue, isBasecampDueToday } from '@/lib/basecamp-calendar';
import { isModClick } from '@/lib/open-intent';
import {
  ChevronRight,
  ExternalLink,
  FileText,
  Loader2,
  ListTodo,
  RefreshCw,
  Star,
} from 'lucide-react';
import type { BasecampAssignment, BasecampTodo } from '@/types/basecamp';

interface RelatedDocument {
  id: string;
  title: string;
  status: string;
  excerpt: string;
  filePath: string;
  projectName?: string;
  updatedAt?: string;
  matchScore?: number;
  primary?: boolean;
  documentIndex?: number | null;
  documentRole?: string | null;
}

function formatActivityDate(createdAt: string): string {
  if (!createdAt) return '';
  const d = new Date(createdAt);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

function formatDueLabel(dueOn?: string | null): string | null {
  if (!dueOn) return null;
  if (isBasecampDueOverdue(dueOn)) return 'Overdue';
  if (isBasecampDueToday(dueOn)) return 'Due today';
  return `Due ${dueOn}`;
}

function formatCreatedLabel(createdAt?: string | null): string | null {
  if (!createdAt) return null;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

const PREVIEW_META_LABEL_CLASS =
  'text-xs font-semibold uppercase tracking-wide text-foreground/70 block mb-0.5';

const PREVIEW_SECTION_LABEL_CLASS =
  'text-xs font-semibold uppercase tracking-wide text-foreground/70';

function PreviewMetaField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <span className={PREVIEW_META_LABEL_CLASS}>{label}</span>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

function sortRelatedDocs(docs: RelatedDocument[]): RelatedDocument[] {
  return [...docs].sort((a, b) => {
    const aIsReadme = a.filePath?.split('/').pop()?.toLowerCase() === 'readme.md';
    const bIsReadme = b.filePath?.split('/').pop()?.toLowerCase() === 'readme.md';
    if (aIsReadme && !bIsReadme) return -1;
    if (!aIsReadme && bIsReadme) return 1;
    if (a.primary && !b.primary) return -1;
    if (!a.primary && b.primary) return 1;
    if (a.documentRole === 'index' && b.documentRole !== 'index') return -1;
    if (b.documentRole === 'index' && a.documentRole !== 'index') return 1;
    if (a.documentIndex != null && b.documentIndex != null) return a.documentIndex - b.documentIndex;
    if (a.documentIndex != null) return -1;
    if (b.documentIndex != null) return 1;
    return (b.matchScore || 0) - (a.matchScore || 0);
  });
}

function pickPrimarySpec(docs: RelatedDocument[]): RelatedDocument | null {
  if (docs.length === 0) return null;
  return sortRelatedDocs(docs).reduce((prev, current) => {
    const currentIsReadme = current.filePath?.split('/').pop()?.toLowerCase() === 'readme.md';
    const prevIsReadme = prev.filePath?.split('/').pop()?.toLowerCase() === 'readme.md';
    if (currentIsReadme && !prevIsReadme) return current;
    if (!currentIsReadme && prevIsReadme) return prev;
    if (current.primary && !prev.primary) return current;
    if (!current.primary && prev.primary) return prev;
    if (current.documentRole === 'index' && prev.documentRole !== 'index') return current;
    if (prev.documentRole === 'index' && current.documentRole !== 'index') return prev;
    if (current.documentIndex != null) {
      if (prev.documentIndex == null) return current;
      if (current.documentIndex < prev.documentIndex) return current;
      if (prev.documentIndex < current.documentIndex) return prev;
    } else if (prev.documentIndex != null) {
      return prev;
    }
    return (current.matchScore || 0) > (prev.matchScore || 0) ? current : prev;
  });
}

function PreviewCrumbSep() {
  return (
    <ChevronRight
      className="h-3 w-3 shrink-0 text-muted-foreground"
      strokeWidth={2.25}
      aria-hidden
    />
  );
}

function PreviewLocationTrail({
  projectName,
  listName,
  projectDisabled,
  listDisabled,
  onProject,
  onList,
}: {
  projectName: string;
  listName: string;
  projectDisabled: boolean;
  listDisabled: boolean;
  onProject: () => void;
  onList: () => void;
}) {
  if (!projectName && !listName) return null;
  return (
    <nav
      className="flex min-w-0 max-w-full items-center gap-0.5 text-xs text-muted-foreground"
      aria-label="Todo location"
      data-testid={testIds.basecamp.todosPreviewBreadcrumb}
    >
      {projectName ? (
        <button
          type="button"
          className="min-w-0 truncate text-left hover:text-foreground hover:underline underline-offset-2 transition-colors disabled:no-underline disabled:hover:text-muted-foreground"
          title={projectName}
          disabled={projectDisabled}
          onClick={onProject}
        >
          {projectName}
        </button>
      ) : null}
      {projectName && listName ? <PreviewCrumbSep /> : null}
      {listName ? (
        <button
          type="button"
          className="min-w-0 truncate text-left hover:text-foreground hover:underline underline-offset-2 transition-colors disabled:no-underline disabled:hover:text-muted-foreground"
          title={listName}
          disabled={listDisabled}
          onClick={onList}
        >
          {listName}
        </button>
      ) : null}
    </nav>
  );
}

function openWorkSpecInTab(filePath: string, openInNewTab = false) {
  window.dispatchEvent(
    new CustomEvent('open-document', {
      detail: { filePath, openInNewTab },
    }),
  );
}

export interface BasecampTodoPreviewPaneProps {
  assignment: BasecampAssignment;
  workSpecStatus?: { status: string; filePath: string } | null;
  lastComment?: { text: string; createdAt: string; creatorName?: string; stale?: boolean } | null;
  onOpen: (event: MouseEvent<HTMLButtonElement>) => void;
  onClose: () => void;
  onRefresh?: () => void;
  onOpenBasecamp?: (url: string) => void;
  isRefreshing?: boolean;
  headerTestId?: string;
}

export function BasecampTodoPreviewPane({
  assignment,
  workSpecStatus,
  lastComment,
  onOpen,
  onClose,
  onRefresh,
  onOpenBasecamp,
  isRefreshing = false,
  headerTestId = testIds.basecamp.todosPreviewHeader,
}: BasecampTodoPreviewPaneProps) {
  const projectId = assignment.project?.id ?? '';
  const todoId = String(assignment.id);
  const appUrl = assignment._app_url ?? assignment.url;
  const projectName = assignment._project_name ?? assignment.project?.name ?? '';
  const listNameFromAssignment = assignment._parent_title ?? '';

  const [todo, setTodo] = useState<BasecampTodo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedDocs, setRelatedDocs] = useState<RelatedDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedSpec, setSelectedSpec] = useState<RelatedDocument | null>(null);
  const [previewContent, setPreviewContent] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  const listId =
    todo?.parent?.id != null
      ? String(todo.parent.id)
      : assignment._todolist_id
        ? String(assignment._todolist_id)
        : null;
  const listName = todo?.parent?.title ?? listNameFromAssignment;

  const openLocationProject = useCallback(() => {
    if (!projectId) return;
    window.dispatchEvent(
      new CustomEvent('integration-view-change', {
        detail: {
          tabId: `basecamp-project-${projectId}`,
          title: projectName || 'Project',
          type: 'integration-basecamp',
          integrationData: {
            integrationId: 'basecamp',
            projectId,
            ...(projectName ? { projectName } : {}),
          },
        },
      }),
    );
  }, [projectId, projectName]);

  const openLocationList = useCallback(() => {
    if (!projectId || !listId) return;
    window.dispatchEvent(
      new CustomEvent('integration-view-change', {
        detail: {
          tabId: `basecamp-todo-list-${listId}`,
          title: listName || 'Todo list',
          type: 'integration-basecamp-todo-list',
          integrationData: {
            integrationId: 'basecamp',
            projectId,
            listId,
            ...(projectName ? { projectName } : {}),
            ...(listName ? { listName } : {}),
          },
        },
      }),
    );
  }, [projectId, listId, listName, projectName]);

  useEffect(() => {
    setSelectedSpec(null);
    setPreviewContent('');
  }, [todoId, projectId]);

  useEffect(() => {
    if (!projectId || !todoId) {
      setTodo(null);
      setLoading(false);
      setError('Missing project or todo id');
      return;
    }

    const ac = new AbortController();
    setLoading(true);
    setError(null);

    basecampApi
      .getTodoById(projectId, todoId)
      .then((data) => {
        if (!ac.signal.aborted) setTodo(data);
      })
      .catch((err: Error) => {
        if (!ac.signal.aborted) {
          setError(err.message || 'Failed to load todo');
          setTodo(null);
        }
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

    return () => ac.abort();
  }, [projectId, todoId]);

  const loadRelatedDocs = useCallback(async () => {
    if (!todoId) return;
    setLoadingDocs(true);
    try {
      const docs = await basecampApi.getTodoRelatedDocuments(todoId, 50);
      setRelatedDocs(Array.isArray(docs) ? docs : []);
    } catch {
      setRelatedDocs([]);
    } finally {
      setLoadingDocs(false);
    }
  }, [todoId]);

  useEffect(() => {
    void loadRelatedDocs();
  }, [loadRelatedDocs]);

  useBasecampTodoRefreshEvents({
    todoId,
    onRefreshRelatedDocs: () => {
      void loadRelatedDocs();
    },
    onFullRefresh: () => {
      if (!projectId || !todoId) return;
      void basecampApi.getTodoById(projectId, todoId, true).then(setTodo).catch(() => undefined);
      void loadRelatedDocs();
    },
  });

  const sortedSpecs = useMemo(() => sortRelatedDocs(relatedDocs), [relatedDocs]);
  const primarySpec = useMemo(() => pickPrimarySpec(relatedDocs), [relatedDocs]);

  const handleSpecClick = useCallback(async (spec: RelatedDocument) => {
    setSelectedSpec(spec);
    setLoadingPreview(true);
    setPreviewContent('');
    try {
      const response = await fetchWithAuth(
        `/api/documents/by-path?path=${encodeURIComponent(spec.filePath)}`,
      );
      if (!response.ok) throw new Error('Failed to load document');
      const doc = await response.json();
      setPreviewContent(doc.content || '');
    } catch {
      setPreviewContent(`# ${spec.title}\n\nCould not load preview. Open in tab to view.`);
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const todoAny = todo as { assignees?: { name?: string }[] } | null;
  const assigneeNames =
    todoAny?.assignees?.map((a) => a.name).filter(Boolean).join(', ') ||
    assignment.assignee?.name ||
    null;
  const dueLabel = formatDueLabel(todo?.due_on ?? assignment.due_on);

  const specStatus = workSpecStatus?.status;
  const specLabel =
    specStatus === 'in_sync'
      ? 'In sync'
      : specStatus === 'stale'
        ? 'Out of date'
        : specStatus === 'none'
          ? 'No spec'
          : relatedDocs.length > 0
            ? `${relatedDocs.length} spec${relatedDocs.length === 1 ? '' : 's'}`
            : null;

  if (selectedSpec) {
    return (
      <div
        className="h-full flex flex-col bg-card overflow-hidden"
        data-testid={testIds.basecamp.todosPreviewPane}
      >
        <PreviewPaneHeader
          row
          icon={<FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
          label="Work Spec Preview"
          chips={
            <span className="text-[10px] px-1.5 py-0.5 rounded border border-border bg-muted text-muted-foreground font-semibold uppercase tracking-wider leading-none flex-shrink-0">
              Document
            </span>
          }
          onOpen={(event) => openWorkSpecInTab(selectedSpec.filePath, isModClick(event))}
          openTitle="Open in tab"
          openTestId={testIds.basecamp.todosPreviewOpen}
          onClose={() => setSelectedSpec(null)}
        />
        <nav
          className="flex min-w-0 shrink-0 items-center gap-1 overflow-hidden border-b border-border px-4 py-2 text-xs text-muted-foreground"
          aria-label="Preview breadcrumb"
          data-testid={testIds.basecamp.todosPreviewBreadcrumb}
        >
          <button
            type="button"
            className="shrink-0 hover:text-foreground transition-colors"
            onClick={() => setSelectedSpec(null)}
          >
            Todo
          </button>
          <PreviewCrumbSep />
          <button
            type="button"
            className="shrink-0 hover:text-foreground transition-colors"
            onClick={() => setSelectedSpec(null)}
          >
            Work Specs
          </button>
          <PreviewCrumbSep />
          <span className="min-w-0 flex-1 truncate text-foreground/80" title={selectedSpec.title}>
            {selectedSpec.title}
          </span>
        </nav>
        {loadingPreview ? (
          <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            Loading preview…
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <MarkdownPreview
              content={previewContent || `# ${selectedSpec.title}\n\nLoading...`}
              className="max-w-none"
              readingColumnPadding="panel"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="h-full flex flex-col bg-card overflow-hidden"
      data-testid={testIds.basecamp.todosPreviewPane}
      aria-label="Todo preview"
    >
      <PreviewPaneHeader
        row
        testId={headerTestId}
        icon={<ListTodo className="h-4 w-4 text-muted-foreground" />}
        chips={
          projectName || listName ? (
            <PreviewLocationTrail
              projectName={projectName}
              listName={listName}
              projectDisabled={!projectId}
              listDisabled={!listId}
              onProject={openLocationProject}
              onList={openLocationList}
            />
          ) : undefined
        }
        onOpen={onOpen}
        openTitle="Open in tab"
        openTestId={testIds.basecamp.todosPreviewOpen}
        onClose={onClose}
      />

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-4 py-4 space-y-4">
          <div className="space-y-3">
            <h3 className="text-base font-semibold leading-snug text-foreground">
              {assignment.title}
            </h3>
            <div
              className="grid grid-cols-2 gap-x-4 gap-y-2.5"
              data-testid={testIds.basecamp.todosPreviewMeta}
            >
              <PreviewMetaField label="Assignee">
                {assigneeNames || (
                  <span className="text-muted-foreground">Unassigned</span>
                )}
              </PreviewMetaField>
              <PreviewMetaField label="List">
                {listName || (
                  <span className="text-muted-foreground">No list</span>
                )}
              </PreviewMetaField>
              <PreviewMetaField label="Due date">
                {todo?.due_on || assignment.due_on ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span>
                      {formatBasecampDueAbsolute(todo?.due_on ?? assignment.due_on)}
                    </span>
                    <BasecampDueBadge
                      dueOn={todo?.due_on ?? assignment.due_on}
                      isOverdue={
                        assignment._is_overdue ??
                        (dueLabel === 'Overdue' ? true : undefined)
                      }
                      statusOnly
                      className={BASECAMP_DUE_BADGE_COMPACT_CLASS}
                    />
                  </span>
                ) : (
                  <span className="text-muted-foreground">No due date</span>
                )}
              </PreviewMetaField>
              <PreviewMetaField label="Created">
                {formatCreatedLabel(todo?.created_at) || (
                  <span className="text-muted-foreground">Unknown</span>
                )}
              </PreviewMetaField>
              <PreviewMetaField label="Work spec">
                {specLabel ? (
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[11px] font-medium',
                      specStatus === 'in_sync' && 'text-green-600 border-green-500/30',
                      specStatus === 'stale' && 'text-amber-600 border-amber-500/30',
                    )}
                  >
                    {specLabel}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">No spec</span>
                )}
              </PreviewMetaField>
            </div>
          </div>

          {lastComment ? (
            <div className="rounded-md border border-border/80 bg-muted/15 px-3 py-2.5 space-y-1">
              <p className={PREVIEW_SECTION_LABEL_CLASS}>
                Last comment
              </p>
              <p className="text-[11px] text-muted-foreground">
                {formatActivityDate(lastComment.createdAt)}
                {lastComment.creatorName ? ` · ${lastComment.creatorName}` : ''}
                {lastComment.stale ? ' (cache)' : ''}
              </p>
              <p className="text-xs leading-snug text-foreground/85 line-clamp-3">
                {lastComment.text}
              </p>
            </div>
          ) : null}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              Loading todo...
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : todo ? (
            <TodoDescription todo={todo} />
          ) : (
              <p className="text-sm text-muted-foreground italic">No description</p>
          )}

          <div className="space-y-2 border-t border-border/60 pt-4">
            <div className="flex items-center justify-between gap-2">
              <p className={PREVIEW_SECTION_LABEL_CLASS}>
                Work Specs{relatedDocs.length > 0 ? ` (${relatedDocs.length})` : ''}
              </p>
              {primarySpec ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[10px] px-2 text-muted-foreground"
                  onClick={() => openWorkSpecInTab(primarySpec.filePath, true)}
                >
                  Open primary
                </Button>
              ) : null}
            </div>

            {loadingDocs ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading work specs...
              </div>
            ) : sortedSpecs.length > 0 ? (
              <div className="space-y-1">
                {sortedSpecs.map((spec) => {
                  const isPrimary = primarySpec?.id === spec.id;
                  return (
                    <button
                      key={spec.id || spec.filePath}
                      type="button"
                      className={cn(
                        'w-full text-left px-2.5 py-2 rounded-md border transition-colors hover:bg-accent/50',
                        isPrimary
                          ? 'bg-muted/40 border-border border-l-2 border-l-primary'
                          : 'bg-transparent border-transparent hover:border-border',
                      )}
                      onClick={() => handleSpecClick(spec)}
                    >
                      <div className="flex items-start gap-2 min-w-0">
                        <FileText
                          className={cn(
                            'h-3.5 w-3.5 mt-0.5 shrink-0',
                            isPrimary ? 'text-foreground/70' : 'text-muted-foreground',
                          )}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-1">
                            <p className="text-xs font-medium line-clamp-2 text-foreground/90">
                              {spec.title}
                            </p>
                            {isPrimary ? (
                              <Badge
                                variant="outline"
                                className="text-[9px] px-1 py-0 h-4 shrink-0 font-normal"
                              >
                                <Star className="h-2 w-2 mr-0.5" />
                                Primary
                              </Badge>
                            ) : spec.status ? (
                              <Badge
                                variant="outline"
                                className="text-[9px] px-1 py-0 h-4 shrink-0 font-normal"
                              >
                                {spec.status}
                              </Badge>
                            ) : null}
                          </div>
                          {spec.excerpt ? (
                            <p className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">
                              {spec.excerpt}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground/70 italic py-1">
                No related work specs found
              </p>
            )}
          </div>
        </div>
      </div>

      {(onRefresh || (appUrl && onOpenBasecamp)) && (
        <div
          className={cn(
            SHELL_COLUMN_FOOTER_CLASS,
            'flex shrink-0 items-center gap-1 px-2 border-t border-border bg-muted/30',
          )}
          data-testid={testIds.basecamp.todosPreviewFooter}
        >
          {onRefresh ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1.5 text-muted-foreground"
              onClick={onRefresh}
              disabled={isRefreshing}
              data-testid={testIds.basecamp.todosPreviewRefresh}
            >
              {isRefreshing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Refresh
            </Button>
          ) : null}
          {appUrl && onOpenBasecamp ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1.5 text-muted-foreground"
              onClick={() => onOpenBasecamp(appUrl)}
              data-testid={testIds.basecamp.todosPreviewOpenBasecamp}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Basecamp
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
